import { Bodytext, CheckboxInput } from '@acorns/web-components'
import styled, { css, keyframes } from 'styled-components'

import Button from 'components/Button'
import { style } from 'theme'

const TitleText = styled.h2`
  font-size: 22px;
  line-height: 30px;
  margin: 30px auto 10px;
  text-align: center;
  font-weight: bold;
  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    margin: 0px auto 10px;
  }
`

const BodyText = styled.p`
  font-size: 14px;
  line-height: 22px;
  margin: 0 auto 40px;
  text-align: center;
  max-width: 520px;
`

const Content = styled.div`
  max-width: 400px;
  margin: 40px auto 80px;

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    margin-bottom: 40px;
  }
`

type CardContainerProps = {
  rotated: boolean
}

const rotateAnimation = keyframes`
  0% { transform:rotate(0deg);}
  30% { transform:rotate(-1deg);}
  70% { transform:rotate(-1deg);}
  100% { transform:rotate(0deg);}
`

const CardBackContainer = styled.div<CardContainerProps>`
  ${props =>
    props.rotated &&
    css`
      animation: ${css`
        ${rotateAnimation} 1.75s
      `};
    `}
  position: relative;
  animation-delay: 0.5s;
  animation-timing-function: ease-in-out;
  width: 312px;
  margin: 45px auto;
`

const CardBack = styled.img`
  max-height: 248px;
  display: block;
  margin: auto;
  position: relative;
  box-shadow: 0 0 40px 18px rgba(0, 0, 0, 0.15);
  border-radius: 14px;
`

const CardOverlayContainer = styled.div`
  position: absolute;
  left: 34px;
  bottom: 30px;
  display: flex;
  flex-direction: column;
  font-size: 8px;
  opacity: 0.75;
  text-transform: uppercase;
`

const Signature = styled.div`
  position: relative;
  max-width: 100%;

  svg {
    height: 29px;
    max-width: 100px;
    width: auto;
  }

  svg * {
    stroke: rgba(255, 255, 255, 0.5);
  }
`

const AgreementCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  max-width: 400px;
  margin-bottom: 80px;

  @media screen and (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    margin-bottom: 40px;
  }
`

const ConfirmScreenRoundCheckboxInput = styled(CheckboxInput)`
  padding-bottom: 0;

  > input + label {
    font-size: 12px;
    line-height: 16px;
    margin: 0;
  }

  > input + label::before {
    border-radius: 50%;
  }

  > label > span {
    text-align: left;
    color: ${props => props.theme.colors.stone};
  }
`

const NextButton = styled(Button)`
  font-weight: 600;
  width: 220px;
  margin: auto;
`

const StyledAnchor = styled.a`
  color: inherit;
  text-decoration: underline;
`

const ApprovalStatusContainer = styled.div`
  display: flex;
  width: 224px;
  text-align: center;
`

const MessageSection = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 10px;
  margin-bottom: 50px;
`

const MessageHeader = styled.div`
  font-weight: 600;
  font-size: 22px;
  line-height: 30px;
`

const MessageSubHeader = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
`

const ErrorImage = styled.img`
  display: block;
  width: 118px;
  margin: 30px auto 40px;
`

const SpinnerContainer = styled.div`
  height: 35px;
  margin-bottom: 26px;
`

const VerificationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 460px;
  background-color: ${style(theme => theme.colors.white)};

  @media screen and (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    min-height: calc(100vh - 80px);
  }
`

const VerificationHeader = styled(Bodytext)`
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
`

const VerificationBody = styled(Bodytext)`
  position: absolute;
  bottom: -10px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: ${style(theme => theme.colors.stone)};

  @media screen and (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    bottom: 20px;
  }
`

const VerifyCardImg = styled.img`
  width: 160px;
  transform: scale(1.5);
  margin: 110px 0 30px;

  @media screen and (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    margin: 105px 0 28px;
  }
`

const VerifyHeading = styled(MessageHeader)`
  margin-bottom: 20px;

  @media screen and (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    margin-bottom: 28px;
  }
`

const GreenSubHeading = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  color: ${style(theme => theme.colors.green)};
  margin-bottom: 5px;
`

const VerifyRegularSubHeading = styled(MessageSubHeader)`
  margin-bottom: 50px;

  @media screen and (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    margin-bottom: 18px;
  }
`

const VerificationCardSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  background-color: ${style(theme => theme.colors.white)};
  border: 1px solid ${style(theme => theme.colors.ivoryLight)};
  border-radius: ${style(theme => theme.borderRadius.large)};

  @media screen and (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    margin-bottom: 36px;
  }
`

const VerificationCard = styled.div<{ border?: boolean }>`
  ${props => props.border && `border-bottom: 1px solid ${props.theme.colors.ivory}`};

  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 23px;
`

const VerifyScreenRoundCheckboxInput = styled(CheckboxInput)`
  padding-bottom: 0;

  > input + label {
    font-size: 14px;
    line-height: 22px;
    margin: 0;
  }

  > input + label::before {
    border-radius: 50%;
  }

  > label > span {
    text-align: left;
    margin-top: 0;
    margin-left: 41px;
    color: ${props => props.theme.colors.slate};
  }

  @media screen and (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    > label > span {
      margin-left: 37px;
    }
  }
`

export {
  TitleText,
  BodyText,
  Content,
  CardBackContainer,
  CardBack,
  CardOverlayContainer,
  Signature,
  AgreementCard,
  ConfirmScreenRoundCheckboxInput,
  NextButton,
  StyledAnchor,
  ApprovalStatusContainer,
  MessageSection,
  MessageHeader,
  MessageSubHeader,
  ErrorImage,
  SpinnerContainer,
  VerificationContainer,
  VerificationBody,
  VerificationHeader,
  VerifyCardImg,
  VerifyHeading,
  GreenSubHeading,
  VerifyRegularSubHeading,
  VerificationCardSection,
  VerificationCard,
  VerifyScreenRoundCheckboxInput,
}
